import { ErrorMessage } from "@hookform/error-message";
import React, { ChangeEvent, FC, FocusEvent, KeyboardEvent, useState } from "react";
import { InputGroup, Input, Button } from "reactstrap";
import { Controller } from "react-hook-form";
import CustomButton from "../customButton/customButton";
import { useTranslation } from "react-i18next";

interface TextFieldProps {
  errors: any;
  value?: any;
  placeholder?: string;
  multiErrorFields?: any[];
  disabled?: boolean;
  type: any;
  textarea?: boolean;
  name: string;
  autoFocus?: boolean;
  handleFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleBlur?: (e: any) => void;
  maxLength?: number;
  minLength?: number;
  onKeyDown?: (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  rules: Record<string, any>;
  formMethod: any; // Replace 'any' with actual type if possible
  rows?: number;
  defaultValue?: any;
  readOnly?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  onClick?: () => void;
  removeFileFunc?: any;
  ref?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isLink?: boolean;
  triggerValidation?: (fieldName: string) => void; // Add a trigger for validation
  leftLabel?: boolean; // New prop for max word validation
  twoDecimal?: boolean; // New prop for max word validation
  threeDecimal?: boolean; // New prop for max word validation
  decimalNotAllowed?: boolean; // New prop for max word validation
  maxWords?: number;
  passwordType?: boolean;
  leftLabelText?: string;
  accept?: any;
  fileValue?: any;
  wordCount?: any;
  minutes?: any;
  hours?: any;
  totalAscent?: any;
  inputMode?: "text" | "none" | "tel" | "url" | "email" | "numeric" | "decimal";
}

const TextField: FC<TextFieldProps> = ({
  errors = {},
  value = "",
  placeholder = "",
  multiErrorFields = [],
  disabled = false,
  type,
  textarea = false,
  name,
  autoFocus = false,
  handleFocus,
  handleBlur,
  maxLength,
  minLength,
  onKeyDown,
  formMethod,
  rows,
  defaultValue = "",
  rules,
  readOnly = false,
  required = true,
  min,
  max,
  onClick,
  isLink = false,
  triggerValidation,
  leftLabel = false,
  maxWords = 10,
  onChange,
  twoDecimal = false,
  threeDecimal = false,
  decimalNotAllowed = false,
  passwordType = false,
  inputMode = "text",
  leftLabelText = "",
  removeFileFunc,
  accept,
  fileValue,
  wordCount,
  minutes = false,
  hours = false,
  totalAscent = false,
}) => {
  const { t } = useTranslation();
  const hasError = errors[name] !== undefined;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileLoading, setFileLoading] = useState<boolean>(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    controllerOnChange: (value: any) => void
  ) => {
    let { value } = e.target;
    const trimmedValue = value.trimLeft();

    if (type === "number") {
      const regex = /^\d*\.?\d*$/;
      if (!regex.test(trimmedValue)) return;
    }

    if (type === "number" && minutes) {
      let numValue = parseFloat(trimmedValue);
      if (!isNaN(numValue) && numValue === 0) value = "0";
      if (!isNaN(numValue) && numValue > 59) value = "59";
    }
    if (type === "number" && hours) {
      let numValue = parseFloat(trimmedValue);
      if (!isNaN(numValue) && numValue === 0) value = "0";
      if (!isNaN(numValue) && numValue > 99) value = "99";
    }
    if (type === "number" && totalAscent) {
      let numValue = parseFloat(trimmedValue);
      if (!isNaN(numValue) && numValue === 0) value = "1";
      if (!isNaN(numValue) && numValue > 9999999) value = "9999999";
    }

    if (type === "number" && twoDecimal) {
      const regex = /^\d*\.?\d{0,2}$/;
      if (!regex.test(trimmedValue)) return;

      if (trimmedValue.includes(".")) {
        const [integerPart, decimalPart] = trimmedValue.split(".");
        value = `${integerPart}.${decimalPart.slice(0, 2)}`;
      }
    }
    if (type === "number" && threeDecimal) {
      const regex = /^\d*\.?\d{0,3}$/;
      if (!regex.test(trimmedValue)) return;
 
      if (trimmedValue.includes(".")) {
        const [integerPart, decimalPart] = trimmedValue.split(".");
        value = `${integerPart}.${decimalPart.slice(0, 3)}`;
      }
    }

    controllerOnChange(value);
    if (onChange) onChange(e);
    if (triggerValidation) triggerValidation(name);
  };

  const handleFileChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: (value: any) => void
  ) => {
    const file = e.target.files ? e.target.files[0] : null;
  
    if (file) {
      // Check if the file has a .gpx extension
      if (!file.name.toLowerCase().endsWith(".gpx")) {
        formMethod.setError(name, {
          type: "manual",
          message: "Only GPX files are allowed.",
        });
  
        // Reset input to prevent non-GPX file selection
        e.target.value = "";
        return;
      }
  
      if (file.size === 0) {
        formMethod.setError(name, {
          type: "manual",
          message: "File is empty (0KB). Please upload a valid file.",
        });
  
        // Reset input if file is empty
        e.target.value = "";
        return;
      }
  
      // Proceed with valid file
      setFileLoading(true);
      const reader = new FileReader();
      reader.onload = () => {
        setFileLoading(false);
        formMethod.clearErrors(name);
        setSelectedFile(file);
        onChange(file);
        if (triggerValidation) triggerValidation(name);
      };
  
      reader.onerror = () => {
        setFileLoading(false);
        formMethod.setError(name, {
          type: "manual",
          message: "Corrupted file detected. Please upload a valid file.",
        });
  
        // Reset input if file is corrupted
        setSelectedFile(null);
        e.target.value = "";
      };
  
      reader.readAsArrayBuffer(file);
    }
  };
  

  const handleRemoveFile = () => {
    formMethod.setValue(name, null, { 
      shouldDirty: true,
      shouldValidate: true 
    });
    console.log('handleRemoveFile', name);
    setSelectedFile(null);
    formMethod.setValue(name, undefined);
    setFileLoading(false);
    if (triggerValidation) triggerValidation(name);
    removeFileFunc?.();
    const fileInput = document.getElementById(name) as HTMLInputElement;
    if (fileInput) fileInput.value = "";
  };

  return (
    <>
      <Controller
        defaultValue={defaultValue}
        rules={rules}
        render={({ field: { onChange, value } }) => (
          <>
            <InputGroup>
              {isLink && (
                <span className="input-group-text">https://www.</span>
              )}
              {type === "file" ? (
                <div className={selectedFile ? "fileInput selected" : "fileInput"}>
                  <span
                    className="btn upload-btn"
                    onClick={() => !selectedFile && document.getElementById(name)?.click()}
                  >
                    {fileLoading ? (
                      <span className="spinner-border spinner-border-sm me-2" />
                    ) : selectedFile ? (
                      selectedFile.name
                    ) : (
                      <>
                        <i className="icon-upload me-2" />
                        {t("UPLOAD")}
                      </>
                    )}
                  </span>
                  <button className="removeBtn">
                    {selectedFile && (
                      <span className="text-danger text-decoration-underline" onClick={handleRemoveFile}>
                        Remove
                      </span>
                    )}
                  </button>
                  <Input
                    type="file"
                    id={name}
                    accept={accept}
                    hidden
                    onChange={(e) => handleFileChange(e, onChange)}
                    disabled={disabled || readOnly}
                  />
                </div>
              ) : (
                <Input
                className={`${hasError || wordCount > maxWords? "is-invalid" : ""}`}
                  name={name}
                  placeholder={placeholder}
                  autoFocus={autoFocus}
                  disabled={disabled} 
                  autoComplete="off"
                  required={required}
                  type={type}
                  inputMode={inputMode}
                  readOnly={readOnly}
                  onBlur={handleBlur}
                  onKeyDown={onKeyDown}
                  maxLength={maxLength}
                  minLength={minLength}
                  min={min}
                  max={max}
                  onFocus={handleFocus}
                  value={formMethod.watch(name) || value || ""}
                  as={textarea ? "textarea" : "input"}
                  rows={rows}
                  onChange={(e) => handleInputChange(e, onChange)}
                  onClick={onClick}
                />
              )}
              {leftLabel && <span className="input-group-text">{leftLabelText}</span>}
            </InputGroup>
            {textarea && (
              <div className="word-count text-end">
                <b className={wordCount > maxWords ? 'red' : ''}>{wordCount}</b> / {maxWords} words
              </div>
            )}
          </>
        )}
        name={name}
        control={formMethod.control}
      />
      {multiErrorFields?.length > 0 ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ messages }) => {
            if (messages) {
              const isMultipleError = Object.keys(messages).every((errKey) =>
                multiErrorFields.some((field:any) => field[errKey] !== undefined)
              );
              if (isMultipleError) {
                return (
                  <div className="errorMsg show passwordcustom">
                    {multiErrorFields.map((fieldError:any, index:any) => {
                      const key = Object.keys(fieldError)[0];
                      const isError = Object.keys(messages).includes(key);
                      return (
                        <div
                          className={isError ? "red" : "green"}
                          key={`error-${index}`}
                        >
                          <i
                            className={
                              isError ? "icon-error_outline" : "icon-checkmark"
                            }
                          />
                          {fieldError[key]}
                        </div>
                      );
                    })}
                  </div>
                );
              }
            }
            return null;
          }}
        />
      ) : hasError ? (
        <div className="errorMsg show">
          <i className="icon-error_outline" />
          {errors[name]?.message}
        </div>
      ) : null}
    </>
  );
};

export default TextField;
